import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ProjectAPI from "@/src/api/ProjectAPI";
import Dialog from "@/src/components/dialog/Dialog";
import { Accordion } from "@/src/components/ui/accordion";
import { Button } from "@/src/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { useStorageAccessMutations } from "@/src/hooks/mutations/useStorageAccessMutations";
import useAbility from "@/src/hooks/useAbility";

import StorageAccessFormDialog from "./StorageAccessFormDialog";
import StorageAccessRow from "./StorageAccessRow";

import type { JSX } from "react";

import type { StorageAccess } from "@/src/api/ProjectAPI.types";
import type { AppState } from "@/src/reducers";

function Placeholder({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="text-muted-foreground px-6.25 py-4 text-sm">{children}</p>
  );
}

export default function StorageAccessConfiguration(): JSX.Element {
  const { t } = useTranslation("editor-organization");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccess, setEditingAccess] = useState<StorageAccess | null>(
    null,
  );
  const [accessToDelete, setAccessToDelete] = useState<StorageAccess | null>(
    null,
  );
  const [testingUuid, setTestingUuid] = useState<string | null>(null);

  const { deleteStorageAccess, testStorageAccessConnection, isDeleting } =
    useStorageAccessMutations();

  const { canAccess } = useAbility();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { storageAccessWrite } = canAccess(orgaOptions);

  const listQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getStorageAccessList,
    params: [],
  });
  const { data, isLoading } = useQuery(listQuery);
  const accessList = data?.data ?? [];

  const handleCreate = (): void => {
    setEditingAccess(null);
    setIsFormOpen(true);
  };

  const handleEdit = (access: StorageAccess): void => {
    setEditingAccess(access);
    setIsFormOpen(true);
  };

  const handleTest = async (access: StorageAccess): Promise<void> => {
    setTestingUuid(access.uuid);
    try {
      await testStorageAccessConnection(access.uuid);
    } catch {
      // The failure toast already fired in the mutation's onError; the list
      // is refetched in onSettled so the status pill reflects the result.
    } finally {
      setTestingUuid(null);
    }
  };

  const handleDeleteConfirm = (): void => {
    if (!accessToDelete) return;
    deleteStorageAccess(accessToDelete.uuid, {
      onSettled: () => setAccessToDelete(null),
    });
  };

  function renderContent(): JSX.Element {
    if (isLoading) {
      return <Placeholder>{t("storageAccess.loading")}</Placeholder>;
    }
    if (accessList.length === 0) {
      return <Placeholder>{t("storageAccess.empty")}</Placeholder>;
    }
    return (
      <Accordion type="multiple">
        {accessList.map((access) => (
          <StorageAccessRow
            key={access.uuid}
            access={access}
            canWrite={storageAccessWrite}
            isTesting={testingUuid === access.uuid}
            onDelete={setAccessToDelete}
            onEdit={handleEdit}
            onTest={handleTest}
          />
        ))}
      </Accordion>
    );
  }

  return (
    <Card className="mt-3.5 gap-0 py-0">
      <CardHeader className="border-b py-4 [.border-b]:pb-4">
        <CardTitle>{t("storageAccess.title")}</CardTitle>
        <CardDescription>{t("storageAccess.subtitle")}</CardDescription>
        {storageAccessWrite ? (
          <CardAction>
            <Button onClick={handleCreate}>
              <Plus />
              {t("storageAccess.addAccess")}
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      {renderContent()}

      <StorageAccessFormDialog
        access={editingAccess}
        onClose={(): void => setIsFormOpen(false)}
        open={isFormOpen}
      />

      <Dialog
        cancelLabel={t("editor-offers:cancel")}
        confirmLabel={t("storageAccess.actions.delete")}
        confirmVariant="destructive"
        contentClassName="sm:max-w-sm"
        isConfirmDisabled={isDeleting}
        isOpen={accessToDelete !== null}
        onConfirm={handleDeleteConfirm}
        title={t("storageAccess.deleteTitle")}
        onOpenChange={(next: boolean): void => {
          if (!next) setAccessToDelete(null);
        }}
      >
        <p className="text-muted-foreground text-sm">
          {t("storageAccess.deleteConfirm", { name: accessToDelete?.label })}
        </p>
      </Dialog>
    </Card>
  );
}
